import { formatCompactMoney } from '../../compact-money';
import {
  ASK_HELIX_DEFAULT_DERIVED_RATIO_PRESENTATION,
  ASK_HELIX_DERIVED_RATIO_PRESENTATIONS,
  type AskHelixDerivedRatioPresentationDto,
  type AskHelixNumericBindingDto,
} from '../../ask-helix';
import {
  addExactDecimal,
  compareExactDecimal,
  EXACT_DECIMAL_ZERO,
  exactDecimalFromText,
  type ExactDecimal,
  divideExactDecimalAtScale,
  exactDecimalFromNumber,
  multiplyExactDecimal,
} from '../../exact-decimal';
import type { NumericBinding } from './numeric-binding';

export type EngineBoundNumericBinding = AskHelixNumericBindingDto | NumericBinding;

/** The single Law 15 anti-fabrication predicate. */
export function numericBindingIsEngineBound(binding: EngineBoundNumericBinding): boolean;
export function numericBindingIsEngineBound(
  binding: EngineBoundNumericBinding, presentation: 'compact_money',
): boolean;
export function numericBindingIsEngineBound(
  binding: EngineBoundNumericBinding, presentation?: 'compact_money',
): boolean {
  return numericBindingFenceFailures(binding, presentation).length === 0;
}

export function numericBindingFenceFailures(
  binding: EngineBoundNumericBinding, presentation?: 'compact_money',
): string[] {
  const missing: string[] = [];
  if (!safeString(binding.placeholder)) missing.push('missing figure key');
  if (
    !safeString(binding.valuePath) &&
    !safeString((binding as { metricKey?: string | null }).metricKey)
  ) {
    missing.push('missing value path or metric key');
  }
  if (presentation === 'compact_money') missing.push(...compactMoneyFailures(binding));
  const scaleFailure = sharePercentScaleFenceFailure(binding);
  if (scaleFailure) missing.push(scaleFailure);
  missing.push(...numericBindingIntegrityFailures(binding));
  return missing;
}

/** A declared compact-money display must be faithful to its exact engine value. */
function compactMoneyFailures(binding: EngineBoundNumericBinding): string[] {
  if (typeof binding.valuePath !== 'string' || binding.valuePath.trim() === '')
    return ['invalid money value path'];
  const exact = binding.exactValue;
  if (binding.format !== 'usd' || binding.unit !== 'USD' ||
      typeof exact !== 'string' || !/^-?\d+(?:\.\d+)?$/u.test(exact) ||
      !Number.isFinite(binding.value) || binding.value !== Number(exact))
    return ['invalid exact money value or presentation'];
  if ((binding.evidence?.kind === 'scenario_option') !== safeString(binding.valuePath).startsWith('scenario:'))
    return ['scenario value path differs from the stated computation'];
  return [1, 2].some((digits) => {
    const expected = formatCompactMoney(exact, digits as 1 | 2);
    return binding.displayValueCompact === expected && binding.displayValue === expected;
  }) ? [] : ['money display differs from the exact engine value'];
}

function sharePercentScaleFenceFailure(binding: EngineBoundNumericBinding): string | null {
  if (!safeString(binding.placeholder).endsWith('_pct_of_gap')) return null;
  return binding.scale === 'fraction' || binding.scale === 'points'
    ? null
    : 'missing declared percent scale';
}

export function numericBindingIntegrityFailures(binding: EngineBoundNumericBinding): string[] {
  if (
    binding.reconciliation?.method === 'sum_of_parts' &&
    binding.reconciliation.exactTotal !== undefined
  )
    return exactSumIntegrityFailures(binding);
  const failures: string[] = [];
  const total = numericBindingTotal(binding);
  const componentSum = reconciliationComponentSum(binding);
  const residual = binding.reconciliation?.residual;
  const residualValue = residual == null ? 0 : Number(residual.value);
  if (binding.reconciliation?.method === 'derived_ratio') {
    failures.push(...derivedRatioIntegrityFailures(binding));
  } else if (binding.reconciliation?.method === 'scaled_by_fraction') {
    failures.push(...scaledByFractionIntegrityFailures(binding));
  } else if (total != null && componentSum != null) {
    const reconciledSum = componentSum + (Number.isFinite(residualValue) ? residualValue : 0);
    if (!amountsReconcile(total, reconciledSum)) {
      failures.push('components do not reconcile to the stated total');
    } else if (Math.abs(residualValue) > 0.01 && !safeString(residual?.label)) {
      failures.push('unlabeled non-zero reconciliation residual');
    }
  } else if (residual && !(Math.abs(residualValue) <= 0.01)) {
    failures.push('unverifiable non-zero reconciliation residual');
  }
  return failures;
}

export function numericBindingTotal(binding: EngineBoundNumericBinding): number | null {
  const raw = [binding.reconciliation?.total, (binding as { value?: unknown }).value].find(
    (value) => Number.isFinite(Number(value))
  );
  if (raw == null) return null;
  return Number(raw);
}

export function reconciliationComponentSum(binding: EngineBoundNumericBinding): number | null {
  const components = binding.reconciliation?.components;
  if (!Array.isArray(components) || components.length === 0) return null;
  let sum = 0;
  for (const component of components) {
    const value = numericComponentValue(component);
    if (value == null) return null;
    sum += value;
  }
  return roundMoney(sum);
}

export function amountsReconcile(left: number, right: number): boolean {
  return Math.abs(roundMoney(left - right)) <= 0.01;
}

export function roundMoney(value: number): number {
  const rounded = Math.round(value * 100) / 100;
  return Math.abs(rounded) <= 0.005 ? 0 : rounded;
}

function derivedRatioIntegrityFailures(binding: EngineBoundNumericBinding): string[] {
  const components = binding.reconciliation?.components;
  const declared = Array.isArray(components) ? components : [];
  const roleOf = (role: string) =>
    declared.filter(
      (component) => safeString((component as { role?: unknown } | null)?.role) === role
    );
  const numerators = roleOf('numerator');
  const denominators = roleOf('denominator');
  if (declared.length !== 2 || numerators.length !== 1 || denominators.length !== 1) {
    return ['a derived ratio declares exactly one numerator and one denominator'];
  }
  const numerator = numericComponentValue(numerators[0]);
  const denominator = numericComponentValue(denominators[0]);
  if (numerator == null || denominator == null) {
    return ['a derived ratio operand is not a governed number'];
  }
  if (denominator === 0) {
    return ['a derived ratio denominator of zero states no quotient'];
  }

  // #5041 - PROVE THE NUMBER THE READER WAS SHOWN.
  //
  // The projection is computed so the DISPLAYED figure carries the canonical presentation
  // scale: divide at that scale plus the presentation's exponent, then shift by the
  // exponent. Multiplying by a power of ten is a scale shift on an exact decimal, so the
  // percentage-point projection is exact - never a float multiply that reintroduces the
  // rounding this contract exists to eliminate.
  const { exponent, scale } = ASK_HELIX_DERIVED_RATIO_PRESENTATIONS[declaredPresentation(binding)];
  const quotient = divideExactDecimalAtScale(
    exactDecimalFromNumber(numerator),
    exactDecimalFromNumber(denominator),
    scale + exponent
  );
  const lawfulProjection = {
    coefficient: quotient.coefficient,
    scale: quotient.scale - exponent,
  };

  // Every figure the receipt states must BE that projection. `value` is what rendered;
  // `total` is what the receipt claims to reconcile. Neither shadows the other any more -
  // when both are present, both are proved, so they cannot disagree.
  const stated: Array<{ label: string; value: number }> = [];
  const publishedValue = (binding as { value?: unknown }).value;
  if (Number.isFinite(Number(publishedValue))) {
    stated.push({ label: 'published value', value: Number(publishedValue) });
  }
  const statedTotal = binding.reconciliation?.total;
  if (Number.isFinite(Number(statedTotal))) {
    stated.push({ label: 'reconciliation total', value: Number(statedTotal) });
  }

  return stated
    .filter(
      (figure) => compareExactDecimal(exactDecimalFromNumber(figure.value), lawfulProjection) !== 0
    )
    .map(
      (figure) =>
        `the stated ratio ${figure.label} is not the lawful projection of its ` +
        'declared operands at the declared presentation scale'
    );
}

/**
 * A RELATIVE TARGET, PROVED AS THE PRODUCT IT IS.
 *
 * The receipt declares exactly one `base` and one `fraction`; the fence
 * multiplies them exactly - decimal multiplication is closed, so coefficients
 * multiply and scales add with no rounding step anywhere - and requires every
 * figure the receipt states to BE that product. `value` is what rendered and
 * `total` is what the receipt claims; when both are present both are proved, so
 * they cannot disagree.
 */
function scaledByFractionIntegrityFailures(binding: EngineBoundNumericBinding): string[] {
  const components = binding.reconciliation?.components;
  const declared = Array.isArray(components) ? components : [];
  const roleOf = (role: string) =>
    declared.filter(
      (component) => safeString((component as { role?: unknown } | null)?.role) === role
    );
  const bases = roleOf('base');
  const fractions = roleOf('fraction');
  if (declared.length !== 2 || bases.length !== 1 || fractions.length !== 1) {
    return ['a scaled value declares exactly one base and one fraction'];
  }
  const base = numericComponentValue(bases[0]);
  const fraction = numericComponentValue(fractions[0]);
  if (base == null || fraction == null) {
    return ['a scaled value operand is not a governed number'];
  }
  const product = multiplyExactDecimal(
    exactDecimalFromNumber(base),
    exactDecimalFromNumber(fraction)
  );

  const stated: Array<{ label: string; value: number }> = [];
  const publishedValue = (binding as { value?: unknown }).value;
  if (Number.isFinite(Number(publishedValue))) {
    stated.push({ label: 'published value', value: Number(publishedValue) });
  }
  const statedTotal = binding.reconciliation?.total;
  if (Number.isFinite(Number(statedTotal))) {
    stated.push({ label: 'reconciliation total', value: Number(statedTotal) });
  }
  return stated
    .filter((figure) => compareExactDecimal(exactDecimalFromNumber(figure.value), product) !== 0)
    .map(
      (figure) =>
        `the stated scaled ${figure.label} is not the exact product of its ` +
        'declared base and fraction'
    );
}

function declaredPresentation(
  binding: EngineBoundNumericBinding
): AskHelixDerivedRatioPresentationDto {
  const declared = binding.reconciliation?.presentation;
  return declared != null && declared in ASK_HELIX_DERIVED_RATIO_PRESENTATIONS
    ? declared
    : ASK_HELIX_DEFAULT_DERIVED_RATIO_PRESENTATION;
}

function numericComponentValue(component: unknown): number | null {
  if (component == null || typeof component !== 'object') return null;
  const values = component as Record<string, unknown>;
  const raw = [
    values.value,
    values.amount,
    values.amountUsd,
    values.valueUsd,
    values.contributionUsd,
    values.netRunRateCostContributionUsd,
    values.leakageUsd,
    values.automatableCostUsd,
  ].find((value) => Number.isFinite(Number(value)));
  return raw == null ? null : Number(raw);
}

function safeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : String(value ?? '').trim();
}

/** Keep decimal parts exact; the numeric fields must remain faithful transport projections. */
function exactReconciliationValue(
  value: number | undefined,
  exact: string | undefined
): ExactDecimal | null {
  if (exact === undefined || !Number.isFinite(value) || Number(exact) !== value) return null;
  return exactDecimalFromText(exact);
}

function exactSumIntegrityFailures(binding: EngineBoundNumericBinding): string[] {
  const receipt = binding.reconciliation;
  const total = exactReconciliationValue(receipt.total ?? undefined, receipt.exactTotal);
  if (!total || !receipt.components?.length) return ['invalid exact reconciliation total or parts'];
  if (binding.value !== undefined && binding.value !== Number(receipt.exactTotal)) {
    return ['published value does not match the exact reconciliation total'];
  }
  if (binding.exactValue !== undefined) {
    const published = exactDecimalFromText(binding.exactValue);
    if (!published || compareExactDecimal(published, total) !== 0) {
      return ['published exact value does not match the reconciliation total'];
    }
  }
  let sum = EXACT_DECIMAL_ZERO;
  for (const component of receipt.components) {
    const part = exactReconciliationValue(component.value, component.exactValue);
    if (!part) return ['invalid exact reconciliation component'];
    sum = addExactDecimal(sum, part);
  }
  if (receipt.residual) {
    const residual = exactReconciliationValue(receipt.residual.value, receipt.residual.exactValue);
    if (!residual) return ['invalid exact reconciliation residual'];
    if (Math.abs(receipt.residual.value) > 0.01 && !safeString(receipt.residual.label)) {
      return ['unlabeled non-zero reconciliation residual'];
    }
    sum = addExactDecimal(sum, residual);
  }
  return compareExactDecimal(total, sum) === 0
    ? []
    : ['components do not reconcile to the stated total'];
}
